import React from "react";
import type {MetaFunction} from "@remix-run/node";
import {json} from "@remix-run/node";
import {useLoaderData} from "@remix-run/react";
import invariant from "tiny-invariant";
import type {AboutUsPage} from "@bettorsignals/api";
import {Collection, isAboutUsPage} from "@bettorsignals/api";
import {$api} from "~/api";

interface LoaderData {
    aboutUsPage: AboutUsPage;
}

export async function loader() {
    const aboutUsResponse = await $api.singleton(Collection.AboutUsPage).read();
    invariant(isAboutUsPage(aboutUsResponse), "Error while loading about us page data");

    return json<LoaderData>({
        aboutUsPage: aboutUsResponse
    });
}

export const meta: MetaFunction = () => {
    return {
        title: "About Us | BettorSignals.com",
        description: "About BettorSignals."
    };
};

export default function AboutUs() {
    const {aboutUsPage} = useLoaderData<LoaderData>();
    return (
        <main className={"mx-auto max-w-bs px-4 xl:px-0"}>
            <div className={"flex flex-col items-center gap-y-3 py-20 md:gap-y-12"}>
                <h1
                    className={
                        "text-center text-4xl font-extrabold text-gray-900 md:text-6xl md:leading-tight"
                    }
                >
                    {"Tune in for"}{" "}
                    <span className={"text-bs-gradient-primary"}>
                        daily betting tips & picks 📡
                    </span>
                </h1>
                <p
                    className={
                        "max-w-3xl text-center text-base font-normal text-gray-500 md:text-xl"
                    }
                >
                    {aboutUsPage.subheading}
                </p>
            </div>

            <div
                className={"prose max-w-none py-4 md:py-8"}
                dangerouslySetInnerHTML={{__html: aboutUsPage.content}}
            />

            <div className={"flex flex-col gap-y-12 py-20"}>
                <h2 className={"text-2xl font-extrabold text-gray-900 md:text-3xl"}>
                    Frequently asked questions
                </h2>
                <div className={"grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-3"}>
                    {aboutUsPage.faq.map((faqItem, index) => (
                        <div className={"flex flex-col gap-y-2"} key={index}>
                            <h3 className={"text-lg font-medium text-gray-900"}>
                                {faqItem.question}
                            </h3>
                            <p className={"text-base text-gray-500"}>{faqItem.answer}</p>
                        </div>
                    ))}
                </div>
            </div>
        </main>
    );
}
